import { useEffect, useState } from "react";
import {
  CheckSquare,
  FileText,
  FolderOpen,
  Search,
  Square,
  X,
} from "lucide-react";
import { Button, Input } from "@/components/ui";
import { useTranslation } from "@/packages/utils";

interface DemoFileMetadata {
  name: string;
  description: string;
  size: number;
  fileType: string;
}

interface DemoFilePickerProps {
  open: boolean;
  onClose: () => void;
  onFilesSelected: (files: File[]) => void;
}

interface DemoFileEntry {
  fileName: string;
  metadata: DemoFileMetadata;
  organization: string;
  fileTypeLabel: string;
  sizeLabel: string;
  searchText: string;
}

function getOrganization(name: string) {
  const [organization] = name.split(" - ");
  return organization?.trim() || name;
}

function formatFileType(fileType: string) {
  return fileType.trim().toUpperCase();
}

function formatFileSize(size: number) {
  return `${(size / 1024).toFixed(1)} KB`;
}

export function DemoFilePicker({
  open,
  onClose,
  onFilesSelected,
}: DemoFilePickerProps) {
  const { t } = useTranslation("documents");
  const [files, setFiles] = useState<Record<string, DemoFileMetadata>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOrganization, setActiveOrganization] = useState<string>("all");

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    setLoading(true);
    fetch("/demo-files/index.json")
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load demo files: ${res.status}`);
        }
        return res.json();
      })
      .then((metadata: Record<string, DemoFileMetadata>) => {
        if (!cancelled) {
          setFiles(metadata);
        }
      })
      .catch((err) => {
        console.error("Failed to load demo files metadata:", err);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setSearchQuery("");
      setActiveOrganization("all");
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  const entries: DemoFileEntry[] = Object.entries(files)
    .map(([fileName, metadata]) => ({
      fileName,
      metadata,
      organization: getOrganization(metadata.name),
      fileTypeLabel: formatFileType(metadata.fileType),
      sizeLabel: formatFileSize(metadata.size),
      searchText:
        `${metadata.name} ${metadata.description} ${metadata.fileType}`.toLowerCase(),
    }))
    .sort((left, right) =>
      left.metadata.name.localeCompare(right.metadata.name),
    );

  const organizations = Array.from(
    new Set(entries.map((entry) => entry.organization)),
  ).sort((left, right) => left.localeCompare(right));

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const visibleEntries = entries.filter((entry) => {
    const matchesOrganization =
      activeOrganization === "all" || entry.organization === activeOrganization;
    const matchesSearch =
      normalizedSearch.length === 0 ||
      entry.searchText.includes(normalizedSearch);
    return matchesOrganization && matchesSearch;
  });

  const allVisibleSelected =
    visibleEntries.length > 0 &&
    visibleEntries.every((entry) => selected.has(entry.fileName));

  const handleToggle = (fileName: string) => {
    const newSelected = new Set(selected);

    if (newSelected.has(fileName)) {
      newSelected.delete(fileName);
    } else {
      newSelected.add(fileName);
    }

    setSelected(newSelected);
  };

  const handleSelectAllVisible = () => {
    const newSelected = new Set(selected);

    for (const entry of visibleEntries) {
      newSelected.add(entry.fileName);
    }

    setSelected(newSelected);
  };

  const handleClearSelection = () => {
    setSelected(new Set());
  };

  const handleConfirm = async () => {
    const filesToAdd: File[] = [];

    for (const fileName of selected) {
      try {
        const response = await fetch(`/demo-files/${fileName}`);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: "application/pdf" });
        filesToAdd.push(file);
      } catch (err) {
        console.error(`Failed to load demo file ${fileName}:`, err);
      }
    }

    if (filesToAdd.length > 0) {
      onFilesSelected(filesToAdd);
    }

    setSelected(new Set());
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in">
      <button
        type="button"
        aria-label={t("upload.demoFiles.closeLabel")}
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-[2px] cursor-default"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("upload.demoFiles.title")}
        className="relative flex h-[min(84vh,760px)] w-full max-w-5xl overflow-hidden rounded-[28px] border border-slate-200/70 bg-[#f5f3ee] shadow-[0_28px_90px_rgba(15,23,42,0.24)]"
      >
        <aside className="hidden w-64 shrink-0 border-r border-slate-200 bg-[#ebe6da] lg:flex lg:flex-col">
          <div className="border-b border-slate-200 px-5 py-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
              {t("upload.demoFiles.libraryLabel")}
            </p>
            <h2 className="mt-2 text-[24px] font-semibold tracking-[-0.03em] text-slate-900">
              {t("upload.demoFiles.title")}
            </h2>
            <p className="mt-2 text-[13px] leading-5 text-slate-600">
              {t("upload.demoFiles.availableCount", { count: entries.length })}
            </p>
          </div>

          <div className="flex-1 px-3 py-4">
            <div className="space-y-1.5">
              <button
                type="button"
                onClick={() => setActiveOrganization("all")}
                className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors ${
                  activeOrganization === "all"
                    ? "bg-white text-slate-950 shadow-sm"
                    : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
                }`}
              >
                <FolderOpen size={16} className="shrink-0" />
                <div className="min-w-0">
                  <p className="truncate text-[14px] font-medium">
                    {t("upload.demoFiles.allFiles")}
                  </p>
                  <p className="text-[12px] text-slate-500">
                    {t("upload.demoFiles.availableCount", {
                      count: entries.length,
                    })}
                  </p>
                </div>
              </button>

              {organizations.map((organization) => {
                const count = entries.filter(
                  (entry) => entry.organization === organization,
                ).length;

                return (
                  <button
                    key={organization}
                    type="button"
                    onClick={() => setActiveOrganization(organization)}
                    className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors ${
                      activeOrganization === organization
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
                    }`}
                  >
                    <FolderOpen size={16} className="shrink-0" />
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-medium">
                        {organization}
                      </p>
                      <p className="text-[12px] text-slate-500">
                        {t("upload.demoFiles.availableCount", { count })}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col bg-[#fcfbf8]">
          <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[12px] font-medium text-slate-500">
                  <FolderOpen size={14} />
                  <span>{t("upload.demoFiles.libraryLabel")}</span>
                  <span>/</span>
                  <span className="truncate text-slate-700">
                    {activeOrganization === "all"
                      ? t("upload.demoFiles.allFiles")
                      : activeOrganization}
                  </span>
                </div>
                <h2 className="mt-2 text-[20px] font-semibold tracking-[-0.03em] text-slate-900 lg:hidden">
                  {t("upload.demoFiles.title")}
                </h2>
                <p className="mt-2 text-[13px] text-slate-600">
                  {t("upload.demoFiles.selectedCount", {
                    count: selected.size,
                  })}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-900"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t("upload.demoFiles.searchPlaceholder")}
                icon={Search}
                className="w-full max-w-xl bg-white"
              />

              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-600">
                  {allVisibleSelected ? (
                    <CheckSquare size={14} className="text-emerald-600" />
                  ) : (
                    <Square size={14} className="text-slate-400" />
                  )}
                  <span>
                    {t("upload.demoFiles.availableCount", {
                      count: visibleEntries.length,
                    })}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSelectAllVisible}
                  disabled={visibleEntries.length === 0 || allVisibleSelected}
                >
                  {t("upload.demoFiles.selectAll")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  disabled={selected.size === 0}
                >
                  {t("upload.demoFiles.clearSelection")}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-hidden px-4 py-4 sm:px-6">
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_10px_40px_rgba(15,23,42,0.05)]">
              <div className="hidden border-b border-slate-200 bg-slate-50 px-4 py-3 md:grid md:grid-cols-[28px_minmax(0,2.4fr)_minmax(180px,1fr)_72px_80px] md:items-center md:gap-3 md:text-[11px] md:font-semibold md:uppercase md:tracking-[0.16em] md:text-slate-500">
                <span className="sr-only">Select</span>
                <span>{t("upload.demoFiles.fileColumn")}</span>
                <span>{t("upload.demoFiles.locationColumn")}</span>
                <span>{t("upload.demoFiles.typeColumn")}</span>
                <span>{t("upload.demoFiles.sizeColumn")}</span>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex h-full items-center justify-center px-6 py-12">
                    <p className="text-[14px] text-slate-500">
                      {t("upload.demoFiles.loading")}
                    </p>
                  </div>
                ) : entries.length === 0 ? (
                  <div className="flex h-full items-center justify-center px-6 py-12">
                    <p className="text-[14px] text-slate-500">
                      {t("upload.demoFiles.empty")}
                    </p>
                  </div>
                ) : visibleEntries.length === 0 ? (
                  <div className="flex h-full items-center justify-center px-6 py-12">
                    <p className="text-[14px] text-slate-500">
                      {t("upload.demoFiles.noMatches")}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {visibleEntries.map((entry) => {
                      const isSelected = selected.has(entry.fileName);

                      return (
                        <label
                          key={entry.fileName}
                          className={`grid cursor-pointer grid-cols-[28px_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-2.5 transition-colors md:grid-cols-[28px_minmax(0,2.4fr)_minmax(180px,1fr)_72px_80px] md:items-center md:gap-y-0 ${
                            isSelected
                              ? "bg-emerald-50/80"
                              : "hover:bg-slate-50/80"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggle(entry.fileName)}
                            className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                          />

                          <div className="flex min-w-0 items-center gap-3">
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border ${
                                isSelected
                                  ? "border-emerald-200 bg-emerald-100 text-emerald-700"
                                  : "border-slate-200 bg-slate-100 text-slate-500"
                              }`}
                            >
                              <FileText size={15} />
                            </div>

                            <div className="min-w-0">
                              <p className="truncate text-[14px] font-medium text-slate-900">
                                {entry.metadata.name}
                              </p>
                              <p className="mt-0.5 line-clamp-1 text-[12px] leading-4 text-slate-500">
                                {entry.metadata.description}
                              </p>
                            </div>
                          </div>

                          <div className="hidden min-w-0 md:block">
                            <p className="truncate text-[13px] leading-4 text-slate-600">
                              {entry.organization}
                            </p>
                          </div>

                          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 md:text-[12px]">
                            {entry.fileTypeLabel}
                          </p>

                          <p className="text-[12px] text-slate-500 md:text-[13px]">
                            {entry.sizeLabel}
                          </p>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[13px] text-slate-600">
                {t("upload.demoFiles.selectedCount", { count: selected.size })}
              </p>

              <div className="flex items-center justify-end gap-2">
                <Button variant="secondary" onClick={onClose}>
                  {t("upload.demoFiles.cancel")}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirm}
                  disabled={selected.size === 0 || loading}
                >
                  {selected.size > 0
                    ? t("upload.demoFiles.addFilesCount", {
                        count: selected.size,
                      })
                    : t("upload.demoFiles.addFiles")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
